/* Copyright (c) 2021 VMware, Inc. All rights reserved. VMware Confidential */

export class SetUtil {
    /**
     * Get the intersection set for two arrays.
     *
     * @param {Array} listA
     * @param {Array} listB
     * @return {Array} of intersection set for listA and listB
     */
    public static intersection(listA: any[], listB: any[]): any[] {
        const setI = new Set();
        const setA = new Set(listA);
        if (Array.isArray(listB)) {
            listB.forEach((elem) => {
                if (setA.has(elem)) {
                    setI.add(elem);
                }
            });
        }
        return Array.from(setI);
    }

    /**
     * Update a source list with new data.
     * This executes in-place merge of two arrays containing items with an ID key.
     *
     * The target list will be updated to only contain items in the source list.
     * Ex: source list: [{id: 1, data: 'new1'}, {id: 2, data: 'new2'}]
     *     target list: [{id: 0, data: 'old0'}, {id: 2, data: 'old2'}]
     *
     * @param target: any[] - list containing new data
     * @param source: any[] - existing list to be modified
     * @param idKey: string - ID key in the array item
     */
    public static inPlaceUpdate(target: any[], source: any[], idKey: string = "id"): void {
        // If source or target is null/undefined, handle appropriately
        if (!source || !target) {
            if (!target) {
                return; // If target is null, nothing to update
            }
            if (!source && target.length > 0) {
                target.splice(0, target.length); // Clear target if source is null
            }
            return;
        }

        // If source array is empty, empty out the target array.
        if (source.length === 0 && target.length > 0) {
            target.splice(0, target.length);
            return;
        }

        // If target array is empty, fill up the target array.
        if (target.length === 0 && source.length > 0) {
            target.splice(0, 0, ...source);
            return;
        }

        let sourceIdx: number = 0;
        let targetIdx: number = 0;

        // Internal compare function.
        function compareDeploymentById(a: any, b: any): number {
            if (a[idKey] < b[idKey]) {
                return -1;
            }
            if (a[idKey] > b[idKey]) {
                return 1;
            }
            return 0;
        }

        // Both arrays are populated. Sort them by ID.
        source.sort(compareDeploymentById);
        target.sort(compareDeploymentById);

        // Add, delete, or modify target items until we've gone through both lists.
        while (sourceIdx < source.length || targetIdx < target.length) {
            const targetItem = target[targetIdx];
            const sourceItem = source[sourceIdx];

            if (!sourceItem) {
                // No more source items to iterate through. Delete rest of target items.
                target.splice(targetIdx, target.length - targetIdx);
                return;
            }

            if (!targetItem) {
                // No more target items to iterate through. Add rest of source items.
                const rest = source.slice(sourceIdx, source.length);
                target.splice(targetIdx, 0, ...rest);
                return;
            }

            if (sourceItem[idKey] < targetItem[idKey]) {
                // Source item is not in target list. Add it to target list.
                target.splice(targetIdx, 0, sourceItem);
                targetIdx++;
                sourceIdx++;
            } else if (sourceItem[idKey] > targetItem[idKey]) {
                // Target item is not in source list. Delete it from target list.
                target.splice(targetIdx, 1);
            } else {
                // Target item exists in source. Copy over source attributes to target object.
                Object.assign(targetItem, sourceItem);
                targetIdx++;
                sourceIdx++;
            }
        }
    }
}
